/**
 * HubAssignmentConfirm — 학생 허브의 시험지 선택·제작 출제가 같이 쓰는 마지막 확인.
 *
 * 확인 중에도 바로 앞에서 검토한 문제 미리보기는 뒤에 그대로 남긴다.
 * 다만 부모 대화상자가 inactive(inert + aria-hidden)를 받아, 초점과 클릭은
 * 이 확인창의 두 선택지에만 머문다. 첫 초점은 DOM 순서상 `돌아가기`다.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Send } from 'lucide-react';
import { Button, Modal } from '../common';

function HubAssignmentConfirm({
  isOpen,
  onClose,
  onConfirm,
  studentName,
  paperTitle,
  problemCount,
  busy = false,
}) {
  const backButtonId = React.useId();
  const close = () => { if (!busy) onClose?.(); };

  // 확정을 누른 버튼은 요청 중 disabled가 된다. 그대로 두면 브라우저에 따라 초점이 body로
  // 떨어질 수 있어, 닫히지는 않지만 초점을 받을 수 있는 「돌아가기」로 즉시 옮긴다.
  React.useLayoutEffect(() => {
    if (isOpen && busy) document.getElementById(backButtonId)?.focus();
  }, [isOpen, busy, backButtonId]);

  // 모바일 내비게이션은 브라우저가 허용하는 최댓값(2147483647)을 쓴다. 같은 최댓값에서
  // body 끝에 붙는 이 portal이 뒤에 그려져 화면 전체를 맡고, 확정 중 다른 학생 이동도 막는다.
  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="시험을 출제할까요?"
      size="sm"
      showCloseButton={false}
      closeOnBackdropClick={!busy}
      closeOnEsc={!busy}
      zIndex={2147483647}
      footer={(
        <>
          <Button
            id={backButtonId}
            variant="secondary"
            onClick={close}
            aria-disabled={busy ? 'true' : undefined}
            className={`min-h-[44px] ${busy ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            돌아가기
          </Button>
          <Button className="min-h-[44px]" onClick={onConfirm} loading={busy} disabled={busy}>
            {busy ? '출제 중…' : '출제 확정'}
          </Button>
        </>
      )}
    >
      <div className="space-y-3" aria-busy={busy ? 'true' : undefined}>
        <span className="sr-only" role="status" aria-live="polite">
          {busy ? '시험을 출제하는 중입니다.' : ''}
        </span>
        <div className="flex items-start gap-3 rounded-2xl bg-primary/5 p-3">
          <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Send className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{studentName || '이 학생'}</p>
            <p className="mt-0.5 break-words text-sm text-gray-700">「{paperTitle || '제목 없음'}」</p>
            <p className="mt-1 text-xs text-gray-500">{Number(problemCount) || 0}문항</p>
          </div>
        </div>
        <p className="text-sm leading-6 text-gray-600">
          확정하면 현재 문항과 본문이 이 학생의 테스트로 고정됩니다.
        </p>
      </div>
    </Modal>
  );
}

HubAssignmentConfirm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  studentName: PropTypes.string,
  paperTitle: PropTypes.string,
  problemCount: PropTypes.number,
  busy: PropTypes.bool,
};

export default HubAssignmentConfirm;
